/**
 * Tracking, in two views of the same trimester.
 *
 * DELIVERY is the roll-up — target, hired, pace, the cluster split, plan versus
 * now. HIRING is the same round role by role: which seats were promised, which
 * were filled, and whether they were filled when they were promised.
 *
 * They are sub-tabs of one another rather than two tabs because they answer the
 * same question at two altitudes, and a number on the first is only ever
 * actionable by finding the rows behind it on the second.
 *
 * The shell is separate from `TrackingTab` deliberately: that component is the
 * delivery view and has its own tests pinning it as such, so wrapping it here
 * keeps the sub-tab routing out of a file that is already at length.
 *
 * ⚠️ **"Where the candidates are" moved INTO `TrackingTab` on Oct 19 2026**, with the tile strip.
 * It had been mounted here precisely because that component returned early on an outage — but a
 * tile cannot open a section that lives in its parent, and the strip is the tab's new shape. What
 * replaced the early return is the thing that made it necessary: `TrackingTab` now renders every
 * section from a `data | null`, so an HDB outage leaves the warehouse sections up and a warehouse
 * outage leaves the delivery figures up. The same property, enforced one level down.
 */
import { Navigate, useNavigate } from 'react-router-dom';
import { logAnalyticsEvent } from '../../../../../analytics/nativeAnalytics';
import { CockpitTabs } from '../cockpitTabs';
import { TrackingTab } from './TrackingTab';
import { HiringTable } from './hiring/HiringTable';

type SubTab = 'delivery' | 'hiring';

const SUB_TABS = [
  { value: 'delivery', label: 'Delivery' },
  { value: 'hiring', label: 'Hiring' },
] as const satisfies readonly { value: SubTab; label: string }[];

const DEFAULT_SUB: SubTab = 'delivery';
const SUB_VALUES = new Set<string>(SUB_TABS.map((s) => s.value));

export function TrackingSection({ period, subtabSlug }: { period: string; subtabSlug?: string }) {
  const navigate = useNavigate();

  /**
   * The default sub-tab is named in the URL, the same one-hop `Navigate replace` the bare
   * `/ta-cockpit` path already takes to its default tab.
   *
   * Not cosmetic: the usage rollups group by path, so a bare `/ta-cockpit/tracking` is
   * indistinguishable from a visit that never chose a sub-tab — and Delivery, the busiest view in
   * the Cockpit, would have been invisible on the usage board while Hiring was counted.
   */
  if (!subtabSlug || !SUB_VALUES.has(subtabSlug)) {
    return <Navigate to={`/ta-cockpit/tracking/${DEFAULT_SUB}`} replace />;
  }
  const sub = subtabSlug as SubTab;

  return (
    <div className="space-y-4">
      <CockpitTabs
        size="sub"
        value={sub}
        tabs={SUB_TABS}
        onChange={(next) => {
          void logAnalyticsEvent('ta_cockpit_subtab_changed', { tab: 'tracking', subtab: next });
          navigate(`/ta-cockpit/tracking/${next}`);
        }}
        ariaLabel="Tracking sections"
      />
      {sub === 'hiring' ? <HiringTable period={period} /> : <TrackingTab period={period} />}
    </div>
  );
}
